import { useCurrentPlayer } from "./hooks/useVideosPreload";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { playerActivationStore, subscribeContentVersion } from "../utils/firebase";
import { fetchAllContent } from "../utils/fetchAllContent";
import { offlineStore } from "../utils/offlineStore";
import { CONTENT_SAFETY_REFETCH_INTERVAL } from "../utils/constants";
import { useVideoPlayerStore } from "../utils/store";
import { useEffect } from "react";
import _ from "lodash";
import type { ISubCategory } from "../entities/category.entity";

type QueryLinksParams = {
	subCategoryId?: string;
	subCategoryPaths?: string[];
	categoryId?: string;
	categoryName?: string;
	groupId: string;
	groupName?: string;
}

export const usePlayerData = () => {
	const currentPlayer = useCurrentPlayer();
	const videoPlayer = useVideoPlayerStore();
	const queryClient = useQueryClient();
	const query = useQuery({
		queryKey: ["playerData", playerActivationStore.activationPassword],
		queryFn: async () => {
			const password = playerActivationStore.activationPassword;
			if (!password) throw new Error("Senha de ativação não encontrada");

			const data = await fetchAllContent(password);
			// Atualiza timestamp de verificação após fetch bem-sucedido
			offlineStore.updateLastVerified();
			return data;
		},
		enabled: Boolean(playerActivationStore.activationPassword),
		refetchInterval: CONTENT_SAFETY_REFETCH_INTERVAL,
		// Kiosk windows can lose focus for days; without this React Query pauses
		// the interval and the content stops refreshing on the TV.
		refetchIntervalInBackground: true,
	});

	// Content changes are pushed through meta/contentVersion, so the TV reacts
	// immediately instead of waiting for the next refetch.
	useEffect(() => {
		return subscribeContentVersion(() => {
			queryClient.invalidateQueries({ queryKey: ["playerData"] });
		});
	}, [queryClient]);

	useEffect(() => {
		if (query.data) {
			videoPlayer.setSplashs(
				query.data.splash?.map((video) => {
					return {
						originalUrl: video.video.url,
					};
				}),
			);
		}
	}, [query.data]);

	return {
		currentPlayer,
		query,
		password: playerActivationStore.activationPassword,
		getVideoByUrl: (url: string) => {
			return query.data?.videos.find((video) => video.video.url === url);
		},
		queryGroupBy({ id, name }: { id: string, name?: string }) {
			return _.find(query.data?.groups, (group) => group.id === id || group.name === name);
		},

		queryCategoryBy({ groupId, groupName, categoryId, categoryName }: { groupId: string, groupName?: string, categoryId?: string, categoryName?: string }) {
			const group = this.queryGroupBy({ id: groupId, name: groupName });
			const category = _.find(group?.categories, ({ id, name }) => id === categoryId || name === categoryName);
			if (category) {
				return category;
			}
			return null;
		},
		querySubCategoryBy({ groupId, groupName, categoryId, categoryName, subCategoryId }: { groupId: string, groupName?: string, categoryId?: string, categoryName?: string, subCategoryId?: string }) {
			const category = this.queryCategoryBy({ groupId, groupName, categoryId, categoryName });
			if (!category) {
				return null;
			}
			const findSubCategoryByIdDeep = (
				list: ISubCategory[] | undefined,
				id: string | undefined,
			): ISubCategory | null => {
				if (!list || !id) return null;

				for (const subCategory of list) {
					if (subCategory.id === id) {
						return subCategory;
					}
					const nested = findSubCategoryByIdDeep(subCategory.subCategories, id);
					if (nested) return nested;
				}

				return null;
			};

			const subCategory = findSubCategoryByIdDeep(category.subCategories, subCategoryId);
			if (subCategory) {
				return subCategory;
			}
			return null;
		},
		queryLinks({
			groupId,
			groupName,
			subCategoryPaths,
		}: QueryLinksParams) {

			const groupLinks = this.queryGroupBy({ id: groupId, name: groupName })?.links || []

			const subCategoryLinks = groupLinks.filter((categoryLink) => {
				const categoryLinkSubCategoryPaths = categoryLink.subCategoryPaths?.map(standardizeSubCategoryPath) || [];
				const querySubCategoryPaths = subCategoryPaths?.map(standardizeSubCategoryPath) || [];
				if (querySubCategoryPaths) {
					return querySubCategoryPaths.some((path) => categoryLinkSubCategoryPaths.includes(path));
				}
				return false;
			});

			return subCategoryLinks;
		},
	};
};

export const standardizeSubCategoryPath = (subCategoryPath: string | string[]) => {
	const raw = Array.isArray(subCategoryPath)
		? subCategoryPath.join(" / ")
		: subCategoryPath;

	const normalized = raw
		.replace(/\s*->\s*/g, "/")
		.split("/")
		.map((part) => part.trim())
		.filter(Boolean)
		.join(" -> ")
		.trim();

	return normalized;
};
